import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { Network as NetworkIcon, Users, Boxes, ShoppingBag, Building2, Store } from "lucide-react";

export default function SuperAdminDashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [networks, setNetworks] = useState([]);

  useEffect(() => {
    api.get("/networks/cross-stats").then((r) => setStats(r.data));
    api.get("/networks").then((r) => setNetworks(r.data));
  }, []);

  const cards = [
    { icon: NetworkIcon, label: t("sa_total_networks"), value: stats?.total_networks, testid: "stat-networks" },
    { icon: Users, label: t("sa_total_tenants"), value: stats?.total_tenants, testid: "stat-tenants" },
    { icon: Building2, label: t("sa_total_casa_madre"), value: stats?.total_casa_madre, testid: "stat-casa-madre" },
    { icon: Store, label: t("sa_total_rivenditori"), value: stats?.total_rivenditori, testid: "stat-rivenditori" },
    { icon: Boxes, label: t("sa_total_prodotti"), value: stats?.total_prodotti, testid: "stat-prodotti" },
    { icon: ShoppingBag, label: t("sa_total_ordini"), value: stats?.total_ordini, testid: "stat-ordini" },
  ];

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="superadmin-dashboard">
        <div className="mb-10">
          <div className="overline text-brand mb-2">SuperAdmin · Cross-network view</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">{t("sa_overview")}</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border rounded-sm overflow-hidden mb-12">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="bg-white p-5" data-testid={c.testid}>
                <Icon className="w-5 h-5 text-brand mb-3" strokeWidth={1.5} />
                <div className="text-3xl font-heading font-bold leading-none">{c.value ?? "—"}</div>
                <div className="text-xs text-muted-foreground mt-2 leading-tight">{c.label}</div>
              </div>
            );
          })}
        </div>

        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl">{t("sa_networks")}</h2>
          <Link
            to="/superadmin/networks/new"
            data-testid="sa-create-network-link"
            className="text-sm px-4 h-10 inline-flex items-center rounded-sm bg-brand hover:bg-brand-hover text-white transition-colors"
          >
            + {t("sa_create_network")}
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {networks.map((n) => (
            <Link
              key={n.id}
              to={`/superadmin/networks/${n.id}`}
              data-testid={`network-row-${n.network_key}`}
              className="admin-card p-6 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="overline text-muted-foreground mb-1">{n.network_key}</div>
                  <h3 className="font-heading font-bold text-2xl">{n.display_name}</h3>
                  <div className="text-sm text-muted-foreground mt-1">{n.main_domain}</div>
                </div>
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {n.status}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3 pt-4 border-t border-border">
                <div>
                  <div className="font-heading font-bold text-xl">{n.stats?.total_tenants || 0}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Tenants</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl">{n.stats?.total_rivenditori || 0}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Riv.</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl">{n.stats?.total_prodotti || 0}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Prod.</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl">{n.stats?.total_ordini || 0}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Ord.</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
